import React from 'react';
import { Link } from 'react-router-dom';
import { formatDecimal } from '../../../utils/format';
import { ProfileManager } from '../../../components/inventory/ProfileManager';
import { InventoryRoastProfileButton } from '../../../components/inventory/InventoryRoastProfileButton';
import { QCStatusButton } from '../../../components/inventory/QCStatusButton';
import type { InventoryLot } from '../../../types/inventory';
import type { GreenBeanProfile } from '../../../types/greenBean';
import { History } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';

interface InventoryLotsSectionProps {
  lots: InventoryLot[];
  loading: boolean;
  error: string;
  greenBeanProfiles: GreenBeanProfile[];
  formatDate: (dateString: string | Date) => string;
  onProfileUpdate: (silent?: boolean) => void;
  onRoastClick: (referenceId: string, label: string) => void;
  getGreenBeanQC: (entityId: string, entityType: 'LotType' | 'SampleType') => boolean | null | undefined;
  onQCClick: (
    type: 'bean' | 'cupping',
    entityId: string,
    entityType: 'LotType' | 'SampleType' | 'ItemType',
    lotId?: string
  ) => void;
  onHistoryClick: (lotId: string, lotNumber: string) => void;
  canWrite?: boolean;
}

export const InventoryLotsSection: React.FC<InventoryLotsSectionProps> = ({
  lots,
  loading,
  error,
  greenBeanProfiles,
  formatDate,
  onProfileUpdate,
  onRoastClick,
  getGreenBeanQC,
  onQCClick,
  onHistoryClick,
  canWrite = true,
}) => {
  const { t } = useLanguage();
  if (loading) {
    return (
      <p className="text-sm text-gray-500 italic" data-test-id="inventory-lots-loading">
        {t('inventory.lotDetail.loadingShort')}
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-500" data-test-id="inventory-lots-error">
        {error}
      </p>
    );
  }

  if (lots.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic" data-test-id="inventory-lots-empty">
        {t('inventory.history.noActiveLots')}
      </p>
    );
  }

  return (
    <table className="min-w-full divide-y divide-gray-200 text-sm" data-test-id="inventory-lots-table">
      <thead>
        <tr>
          <th className="px-4 py-2 text-left font-medium text-gray-500">{t('inventory.history.lotNumber')}</th>
          <th className="px-4 py-2 text-right font-medium text-gray-500">{t('inventory.lots.remainingQty')}</th>
          <th className="px-4 py-2 text-left font-medium text-gray-500">{t('inventory.lotDetail.fields.expiryDate')}</th>
          <th className="px-4 py-2 text-left font-medium text-gray-500">{t('inventory.lotDetail.fields.receivedDate')}</th>
          <th className="px-4 py-2 text-center font-medium text-gray-500">{t('purchasing.sampleList.table.greenBeanProfile')}</th>
          <th className="px-4 py-2 text-center font-medium text-gray-500">{t('purchasing.sampleList.table.roastProfile')}</th>
          <th className="px-4 py-2 text-center font-medium text-gray-500">{t('purchasing.sampleList.table.tasteProfile')}</th>
          <th className="px-4 py-2 font-medium text-center text-gray-500">{t('purchasing.sampleList.table.qualityControl')}</th>
          <th className="px-4 py-2 text-center font-medium text-gray-500">{t('settingsMcp.table.actions')}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {lots
          .filter(lot => lot.sourceType !== 'work_order')
          .map(lot => (
            <tr key={lot.id} data-test-id={`inventory-lot-row-${lot.id}`}>
              <td className="px-4 py-2 font-medium text-gray-900">
                <Link to={`/inventory/lots/${lot.id}`} className="text-primary-600 hover:text-primary-800 hover:underline">
                  {lot.lotNumber}
                </Link>
              </td>
              <td className="px-4 py-2 text-right text-gray-900">{formatDecimal(lot.remainingQuantity, 3)}</td>
              <td className="px-4 py-2 text-gray-500">{lot.expiryDate ? formatDate(new Date(lot.expiryDate)) : t('settingsMcp.values.none')}</td>
              <td className="px-4 py-2 text-gray-500">{lot.receivedDate ? formatDate(new Date(lot.receivedDate)) : t('settingsMcp.values.none')}</td>
              <td className="px-4 py-2 text-gray-500 text-center">
                <ProfileManager
                  entityType="LotType"
                  entityId={lot.id}
                  profileType="greenBean"
                  existingProfile={greenBeanProfiles.find(profile => profile.lot_id === lot.id)}
                  onProfileUpdate={() => onProfileUpdate(true)}
                />
              </td>
              <td className="px-4 py-2 text-gray-500 text-center">
                <InventoryRoastProfileButton
                  referenceId={lot.id}
                  dataTestId={`lot-roast-profile-button-${lot.id}`}
                  onClick={() => onRoastClick(lot.id, `Roast Profile – Lot ${lot.lotNumber}`)}
                  readOnly={!canWrite}
                />
              </td>
              <td className="px-4 py-2 text-gray-500 text-center">
                <ProfileManager
                  entityType="LotType"
                  entityId={lot.id}
                  profileType="cupping"
                  onProfileUpdate={() => onProfileUpdate(true)}
                />
              </td>
              <td className="px-4 py-2 text-center space-x-2">
                <QCStatusButton
                  type="Bean"
                  status={getGreenBeanQC(lot.id, 'LotType')}
                  onClick={() => onQCClick('bean', lot.id, 'LotType')}
                />
              </td>
              <td className="px-4 py-2 text-center">
                <button
                  data-test-id={`inventory-lot-history-${lot.id}`}
                  onClick={() => onHistoryClick(lot.id, lot.lotNumber)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                  title={t('inventory.lots.viewHistoryAdjustStock')}
                >
                  <History className="w-4 h-4" />
                  {t('inventory.roastedBulk.history')}
                </button>
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  );
};
